import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, MinusIcon } from '@heroicons/react/24/solid';

const WeekOverWeekComparison = ({ comparisonData }) => {
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(value);
    };

    const formatPercent = (value) => {
        return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
    };

    const getDeltaIcon = (value) => {
        if (value > 0) return <ArrowUpIcon className="w-4 h-4" />;
        if (value < 0) return <ArrowDownIcon className="w-4 h-4" />;
        return <MinusIcon className="w-4 h-4" />;
    };

    const getDeltaColor = (value, isExpenseRelated = false) => {
        // For expenses/charges, decreases are good (green), increases are warning (red)
        // For income/payout, increases are good (green), decreases are bad (red)
        if (value === 0) return 'text-gray-500';

        if (isExpenseRelated) {
            return value > 0 ? 'text-red-600' : 'text-green-600';
        } else {
            return value > 0 ? 'text-green-600' : 'text-red-600';
        }
    };

    if (!comparisonData || !comparisonData.cars) {
        return <div className="text-center text-gray-500">No comparison data available</div>;
    }

    return (
        <div className="space-y-6">
            {/* Week Labels */}
            <div className="flex items-center justify-center gap-4 text-lg font-semibold">
                <span className="text-gray-600">{comparisonData.previous_week}</span>
                <span className="text-[#FF5733]">→</span>
                <span className="text-[#FF5733]">{comparisonData.current_week}</span>
            </div>

            {/* Aggregate Comparison */}
            <div className="bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#2a2e30] mb-4">Overall Performance</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(comparisonData.aggregates).map(([key, data]) => (
                        <div key={key} className="bg-white rounded-lg p-4 shadow">
                            <div className="text-sm text-gray-600 mb-2 capitalize">
                                {key.replace(/_/g, ' ')}
                            </div>
                            <div className="text-lg font-bold text-[#2a2e30] text-right mb-1">
                                {formatCurrency(data.current)}
                            </div>
                            <div className="flex items-center justify-end gap-1 text-sm">
                                <span className={getDeltaColor(data.absoluteChange, key.includes('charge'))}>
                                    {getDeltaIcon(data.absoluteChange)}
                                </span>
                                <span className={getDeltaColor(data.absoluteChange, key.includes('charge'))}>
                                    {formatCurrency(Math.abs(data.absoluteChange))}
                                </span>
                                <span className="text-gray-500">
                                    ({formatPercent(data.percentChange)})
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Per-Car Comparison */}
            <div>
                <h3 className="text-xl font-bold text-[#2a2e30] mb-4">Vehicle Breakdown</h3>
                <div className="space-y-4">
                    {comparisonData.cars.map((car) => (
                        <div key={car.plate_no} className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                <h4 className="font-bold text-lg text-[#2a2e30]">
                                    {car.plate_no} {car.make && `- ${car.make} ${car.model}`}
                                </h4>
                            </div>
                            <div className="p-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <ComparisonMetric
                                        label="Working Days"
                                        data={car.working_days}
                                        formatValue={(v) => v.toFixed(0)}
                                        isExpenseRelated={false}
                                    />
                                    <ComparisonMetric
                                        label="Remittance"
                                        data={car.total_remittance}
                                        formatValue={formatCurrency}
                                        isExpenseRelated={false}
                                    />
                                    <ComparisonMetric
                                        label="Charges"
                                        data={car.total_charges}
                                        formatValue={formatCurrency}
                                        isExpenseRelated={true}
                                    />
                                    <ComparisonMetric
                                        label="Net Income"
                                        data={car.net_income}
                                        formatValue={formatCurrency}
                                        isExpenseRelated={false}
                                    />
                                    <ComparisonMetric
                                        label="Owner Payout"
                                        data={car.owner_payout}
                                        formatValue={formatCurrency}
                                        isExpenseRelated={false}
                                    />
                                    <ComparisonMetric
                                        label="WKI Total"
                                        data={car.wki_total}
                                        formatValue={formatCurrency}
                                        isExpenseRelated={false}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const ComparisonMetric = ({ label, data, formatValue, isExpenseRelated }) => {
    const getDeltaColor = (value) => {
        if (value === 0) return 'text-gray-500';
        if (isExpenseRelated) {
            return value > 0 ? 'text-red-600' : 'text-green-600';
        } else {
            return value > 0 ? 'text-green-600' : 'text-red-600';
        }
    };

    const getDeltaIcon = (value) => {
        if (value > 0) return <ArrowUpIcon className="w-3 h-3" />;
        if (value < 0) return <ArrowDownIcon className="w-3 h-3" />;
        return <MinusIcon className="w-3 h-3" />;
    };

    return (
        <div className="border border-gray-200 rounded-lg p-3">
            <div className="text-xs text-gray-600 mb-1">{label}</div>
            <div className="font-bold text-[#2a2e30] text-right mb-1">
                {formatValue(data.current)}
            </div>
            <div className="flex items-center justify-end gap-1 text-xs">
                <span className={getDeltaColor(data.absoluteChange)}>
                    {getDeltaIcon(data.absoluteChange)}
                </span>
                <span className={getDeltaColor(data.absoluteChange)}>
                    {formatValue(Math.abs(data.absoluteChange))}
                </span>
                {data.percentChange !== 0 && (
                    <span className="text-gray-500">
                        ({(data.percentChange > 0 ? '+' : '')}{data.percentChange.toFixed(1)}%)
                    </span>
                )}
            </div>
            <div className="text-xs text-gray-500 mt-1">
                Previous: {formatValue(data.previous)}
            </div>
        </div>
    );
};

export default WeekOverWeekComparison;
